import { template as _$template } from "solid-js/web";
import { setAttribute as _$setAttribute } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { use as _$use } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<p class=map-overlay__status>Unfolding the map…`),
  _tmpl$2 = /*#__PURE__*/_$template(`<p class="map-overlay__status map-overlay__status--error">The map could not be read: `),
  _tmpl$3 = /*#__PURE__*/_$template(`<div class=map-overlay role=dialog aria-label=Map><canvas class=map-overlay__canvas>`);
import { createEffect, createResource, createSignal, onCleanup, onMount, Show } from "solid-js";
import { paintOps } from "../shell/render/canvas-painter.ts";
import { PANEL_SHEET, PANEL_TITLE_FONT } from "../shell/render/panel-chrome.ts";
import { createShellSurface } from "../shell/render/shell-surface.ts";
import { loadShellAssets } from "../shell/render/shell-assets.ts";
import { browserSettingsStore } from "../audio/settings.ts";
import { currentRealism } from "../mission/realism-settings.ts";
import { regionBakeFor } from "../world/region-catalog.ts";
import { loadWorldRegion } from "../world/region-loader.ts";
import { loadRegionGround } from "../world/region-ground.ts";
import { buildMapModel } from "./map-model.ts";
import { loadMapTerrain } from "./map-network.ts";
import { paintMapPaper } from "./map-paint.ts";
import { createRaster, fillRect, line, rasterToImageData } from "./map-raster.ts";
import { PAPER } from "./map-layout.ts";
import { inkKeySwatches, mapChromeOps, mapOverlayOps, PAPER_RECT } from "./map-screen.ts";
/** The pilot's mark blinks at 1 Hz — half a second on, half a second off. */
const BLINK_PERIOD_MS = 500;

/**
 * Loads everything the sheet needs, once. The region JSON and every baked
 * model the network is read out of are already in `region-loader.ts`'s cache
 * and the browser's HTTP cache respectively — `/fly` fetched them to build the
 * scenery — so opening the map costs parsing, not network.
 */
async function loadMapSheet(region, mission) {
  const entry = regionBakeFor(region);
  const [body, assets] = await Promise.all([loadWorldRegion(entry.path), loadShellAssets({
    images: [],
    sprites: [PANEL_SHEET]
  })]);
  // The same `RegionGround` `/fly` recentred the scenery on — the sheet and
  // the world it is a map of must not disagree about the origin, and since
  // `docs/engineering/london-sea-placements.md` that origin depends on where
  // the water is. `loadRegionGround` caches, so this is the answer `/fly`
  // already computed rather than a second one.
  const ground = await loadRegionGround(body, entry.startAerodrome);
  const model = buildMapModel(body, entry.startAerodrome, mission, ground);
  return {
    model,
    terrain: await loadMapTerrain(body.placements, model.origin),
    assets
  };
}

/**
 * The marginal ink key's colour swatches. They go on as `putImageData` rather
 * than as shell `fill` ops because half of them are `map-raster.ts`'s synthetic
 * indices — water blue and wood green live past `PS.PAL`'s 256, and the shell's
 * painter resolves an index straight against the 256-entry palette. Blitting
 * them through the map's own raster is what guarantees a swatch is exactly the
 * colour the sheet inks that class in, rather than an approximation somebody
 * has to keep in step by hand.
 */
function paintInkKey(context, palette, swatches) {
  for (const {
    rect,
    entry
  } of swatches) {
    // A scrap of the map's own paper with the class's ink on it — see
    // `map-screen.ts`'s `InkKeyEntry` for why it is not a bare colour block.
    const raster = createRaster(rect.w, rect.h, PAPER);
    fillRect(raster, 1, 1, rect.w - 2, rect.h - 2, entry.ink);
    if (entry.line !== undefined) line(raster, 1, Math.floor(rect.h / 2), rect.w - 2, Math.floor(rect.h / 2), entry.line);
    context.putImageData(rasterToImageData(raster, palette), rect.x, rect.y);
  }
}
/**
 * The in-flight map, drawn as one of the original shell's own 320×200 panel
 * screens over the flight view (`M` opens and closes it; `routes/Fly.tsx`
 * pauses the sim while it is up).
 *
 * **This is a port extension, and the sheet says so.** The 1990 game has no
 * in-flight map: `RB.EXE` carries no map module, no map resource and no map
 * key, and the Quick Reference Card lists none — the maps were five fold-out
 * sheets in the box, and `PS.EXE`'s `s\bigmap.c` MAP OF THE FRONT is a
 * career-menu screen, not something you can reach from a cockpit. A browser
 * player has no box, so the port draws the sheet he is missing, in the shell's
 * own visual language, and labels it.
 *
 * The component is a thin shell around the pure modules beside it: it owns the
 * canvas, the one-time load, and the repaint. Nothing here decides what the map
 * looks like.
 */
export function MapOverlay(props) {
  const surface = createShellSurface();
  const [sheet] = createResource(() => ({
    region: props.region,
    mission: props.mission
  }), key => loadMapSheet(key.region, key.mission));
  let canvasRef;
  /** The blink phase of the pilot's mark — the one thing on the sheet that moves. */
  const [blinkOn, setBlinkOn] = createSignal(true);

  // The sim is paused while the map is up, so the sheet would otherwise be a
  // frozen frame. This interval is the map's only clock: it toggles the mark
  // in and out of view so the pilot can find himself at a glance.
  createEffect(() => {
    if (!props.open) {
      setBlinkOn(true);
      return;
    }
    const timer = setInterval(() => setBlinkOn(visible => !visible), BLINK_PERIOD_MS);
    onCleanup(() => clearInterval(timer));
  });
  onMount(() => {
    if (!canvasRef) return;
    surface.attach(canvasRef);
    const observer = new ResizeObserver(() => surface.blit());
    observer.observe(canvasRef);
    onCleanup(() => observer.disconnect());
  });
  createEffect(() => {
    const data = sheet();
    const player = props.player;
    const hidePosition = props.hidePosition;
    const blink = blinkOn();
    if (!props.open || !data || !surface.canvas()) return;
    const titleFont = data.assets.fonts.get(PANEL_TITLE_FONT);
    const labelFont = data.assets.fonts.get("FONT3");
    if (!titleFont || !labelFont) return;
    paintOps(surface.logical, data.assets, mapChromeOps(data.model, titleFont));
    const paper = paintMapPaper({
      model: data.model,
      terrain: data.terrain,
      player,
      hidePosition,
      blinkOn: blink
    });
    surface.logical.context.putImageData(rasterToImageData(paper, data.assets.palette), PAPER_RECT.x, PAPER_RECT.y);
    paintOps(surface.logical, data.assets, mapOverlayOps(data.model, player, hidePosition, labelFont));
    paintInkKey(surface.logical.context, data.assets.palette, inkKeySwatches(data.model, player, hidePosition));
    surface.blit();
  });
  return (() => {
    var _el$ = _tmpl$3(),
      _el$2 = _el$.firstChild;
    var _ref$ = canvasRef;
    typeof _ref$ === "function" ? _$use(_ref$, _el$2) : canvasRef = _el$2;
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return _$memo(() => !!props.open)() && sheet.loading;
      },
      get children() {
        return _tmpl$();
      }
    }), null);
    _$insert(_el$, _$createComponent(Show, {
      get when() {
        return _$memo(() => !!props.open)() && sheet.error;
      },
      get children() {
        var _el$4 = _tmpl$2(),
          _el$5 = _el$4.firstChild;
        _$insert(_el$4, () => String(sheet.error), null);
        return _el$4;
      }
    }), null);
    _$effect(_p$ => {
      var _v$ = !props.open,
        _v$2 = !props.open;
      _v$ !== _p$.e && _el$.classList.toggle("map-overlay--hidden", _p$.e = _v$);
      _v$2 !== _p$.t && _$setAttribute(_el$, "aria-hidden", _p$.t = _v$2);
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$;
  })();
}

/**
 * The map as a flight route mounts it: nothing at all until the player has
 * pressed `M` once, then a permanently mounted sheet that hides itself
 * (`MapOverlayProps.open`).
 *
 * REAL NAVIGATION is read here, from the Realism Panel's own store, for the
 * same reason `routes/Fly.tsx` reads SUN BLIND SPOT from it — the panel is not
 * editable mid-flight, so neither needs a signal.
 */
export function FlightMap(props) {
  return _$createComponent(Show, {
    get when() {
      return props.session.everOpened();
    },
    get children() {
      return _$createComponent(MapOverlay, {
        get open() {
          return props.session.open();
        },
        get region() {
          return props.mission?.region;
        },
        get mission() {
          return props.mission;
        },
        get player() {
          return props.session.player();
        },
        get hidePosition() {
          return currentRealism(browserSettingsStore()).extra.realNavigation;
        }
      });
    }
  });
}